import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PLATFORMS, PLATFORM_LABELS } from '@affily/shared/constants';
import { formatPercent, formatCount } from '@affily/shared/money';
import { Seo } from '../../components/Seo.jsx';
import { Button, Field, Input, Select, Unknown } from '../../components/ui.jsx';

/**
 * Free engagement-rate calculator. No signup, no email gate — this one is pure
 * acquisition, and gating it would defeat the point.
 *
 * The benchmark bands are stated as rules of thumb rather than dressed up as
 * data we measured, which is the same standard applied to every other number
 * on the site.
 */

/** Widely-cited rules of thumb by audience size. Not Affily measurements. */
const BENCHMARKS = [
  { max: 10_000, label: 'Nano (under 10k)', typical: [2.5, 6] },
  { max: 100_000, label: 'Micro (10k–100k)', typical: [1.5, 4] },
  { max: 1_000_000, label: 'Mid (100k–1M)', typical: [1, 2.5] },
  { max: Infinity, label: 'Macro (1M+)', typical: [0.8, 1.8] },
];

const METHODS = {
  followers: {
    label: 'By followers',
    hint: 'Total interactions ÷ followers. The standard for Instagram and TikTok.',
    denominatorLabel: 'Followers',
  },
  reach: {
    label: 'By reach / views',
    hint: 'Total interactions ÷ people who actually saw the post. Usually higher, and more honest.',
    denominatorLabel: 'Reach or views',
  },
};

export default function EngagementRateCalculator() {
  const [platform, setPlatform] = useState('instagram');
  const [method, setMethod] = useState('followers');
  const [form, setForm] = useState({
    denominator: '',
    likes: '',
    comments: '',
    shares: '',
    saves: '',
  });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const result = useMemo(() => {
    const denominator = Number(form.denominator) || 0;
    const interactions =
      (Number(form.likes) || 0) +
      (Number(form.comments) || 0) +
      (Number(form.shares) || 0) +
      (Number(form.saves) || 0);

    // Same rule as everywhere else in Affily: an empty denominator means the
    // answer is unknown, not zero.
    if (denominator <= 0) return { rate: null, interactions, denominator };
    return { rate: (interactions / denominator) * 100, interactions, denominator };
  }, [form]);

  const band = BENCHMARKS.find((b) => (Number(form.denominator) || 0) <= b.max);
  const verdict = (() => {
    if (result.rate === null || !band) return null;
    const [low, high] = band.typical;
    if (result.rate >= high) return { tone: 'positive', text: 'Above the typical range' };
    if (result.rate >= low) return { tone: 'neutral', text: 'Within the typical range' };
    return { tone: 'caution', text: 'Below the typical range' };
  })();

  return (
    <>
      <Seo
        title="Free engagement rate calculator"
        description="Work out your engagement rate across Instagram, TikTok and YouTube, and see how it compares. Free, no signup."
      />

      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
        <p className="text-2xs font-semibold uppercase tracking-widest text-accent">Free tool</p>
        <h1 className="mt-2 text-4xl font-semibold tracking-tight">Engagement rate calculator</h1>
        <p className="mt-3 max-w-xl text-ink-muted">
          Add one post’s numbers and get your rate. No signup, no email, nothing to download.
        </p>

        <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <form className="space-y-5" onSubmit={(e) => e.preventDefault()}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Platform">
                <Select value={platform} onChange={(e) => setPlatform(e.target.value)}>
                  {PLATFORMS.slice(0, 8).map((p) => (
                    <option key={p} value={p}>
                      {PLATFORM_LABELS[p]}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Method" hint={METHODS[method].hint}>
                <Select value={method} onChange={(e) => setMethod(e.target.value)}>
                  {Object.entries(METHODS).map(([key, m]) => (
                    <option key={key} value={key}>
                      {m.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            <Field label={METHODS[method].denominatorLabel} required>
              <Input
                type="number"
                min="0"
                value={form.denominator}
                onChange={set('denominator')}
                placeholder="24000"
              />
            </Field>

            <fieldset>
              <legend className="mb-2 text-sm font-medium">Interactions on the post</legend>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Likes">
                  <Input type="number" min="0" value={form.likes} onChange={set('likes')} />
                </Field>
                <Field label="Comments">
                  <Input type="number" min="0" value={form.comments} onChange={set('comments')} />
                </Field>
                <Field label="Shares">
                  <Input type="number" min="0" value={form.shares} onChange={set('shares')} />
                </Field>
                <Field label="Saves">
                  <Input type="number" min="0" value={form.saves} onChange={set('saves')} />
                </Field>
              </div>
            </fieldset>

            <button
              type="button"
              onClick={() =>
                setForm({ denominator: '', likes: '', comments: '', shares: '', saves: '' })
              }
              className="text-sm text-ink-muted hover:text-ink hover:underline"
            >
              Reset
            </button>
          </form>

          <aside className="lg:sticky lg:top-20 lg:self-start">
            <div className="rounded-lg border border-rule bg-paper-raised p-5">
              <p className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
                Engagement rate
              </p>
              <p className="mt-1 text-4xl font-semibold tabular-nums text-accent">
                {result.rate === null ? (
                  <Unknown reason={`Enter your ${METHODS[method].denominatorLabel.toLowerCase()}`} />
                ) : (
                  formatPercent(result.rate)
                )}
              </p>

              <table className="mt-4 w-full border-t border-rule pt-3 text-sm">
                <tbody>
                  <tr>
                    <td className="py-1 text-ink-muted">Total interactions</td>
                    <td className="py-1 text-right tabular-nums">
                      {formatCount(result.interactions)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1 text-ink-muted">{METHODS[method].denominatorLabel}</td>
                    <td className="py-1 text-right tabular-nums">
                      {result.denominator > 0 ? formatCount(result.denominator) : '—'}
                    </td>
                  </tr>
                </tbody>
              </table>

              {verdict && band && (
                <div className="mt-4 border-t border-rule pt-3">
                  <p
                    className={`text-sm font-medium ${
                      verdict.tone === 'positive'
                        ? 'text-positive'
                        : verdict.tone === 'caution'
                          ? 'text-caution'
                          : 'text-ink'
                    }`}
                  >
                    {verdict.text}
                  </p>
                  <p className="mt-1 text-xs text-ink-faint">
                    {band.label} accounts typically sit between {band.typical[0]}% and{' '}
                    {band.typical[1]}%. These are widely-cited rules of thumb, not numbers Affily
                    measured — treat them as a rough guide.
                  </p>
                </div>
              )}
            </div>

            <div className="mt-4 rounded-lg border border-rule bg-paper-sunken p-4">
              <p className="text-sm font-medium">Put this on a public profile</p>
              <p className="mt-1 text-xs text-ink-muted">
                Brands filter by engagement rate on Affily. Publishing yours, with a link to the
                profile it came from, is how it gets believed.
              </p>
              <Button to="/signup?role=creator" size="sm" className="mt-3">
                Create a free profile
              </Button>
            </div>
          </aside>
        </div>

        <section className="mt-16 max-w-2xl">
          <h2 className="text-xl font-semibold tracking-tight">Which method should you use?</h2>
          <p className="mt-3 text-ink-muted">
            Engagement by followers is the number most brands ask for, because it is the one
            everybody can compute from the outside. Engagement by reach is the more useful number —
            it tells you how the people who actually saw a post responded — but only you can see it,
            so quote it alongside the follower figure rather than instead of it.
          </p>
          <p className="mt-3 text-ink-muted">
            Whichever you publish, say which one it is. A rate quoted without its denominator is
            the kind of number that gets discounted on sight.
          </p>
          <Link
            to="/tools/media-kit-generator"
            className="mt-4 inline-block text-sm font-medium text-accent hover:underline"
          >
            Build a media kit with these numbers →
          </Link>
        </section>
      </div>
    </>
  );
}
